import { FormField } from "../../../interfaces/User.interface";
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import PortraitOutlinedIcon from '@mui/icons-material/PortraitOutlined';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import React, { useEffect, useMemo, useState } from "react";
import ChatIcon from '@mui/icons-material/Chat';
import TransgenderIcon from '@mui/icons-material/Transgender';
import { FormBuilder } from "../../../utils/FormBuilder/FormBuilder";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Chip, Stack, Typography, Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper } from "@mui/material";
import { FormSection, SimpleFormSection, TableSection } from "../../../components/Sections";
import { useFetch } from "../../../utils/FetchTemplate";
import { PhoneInputMask } from "../../../utils/FormatedInputMask";
import { generateValidationSchema } from "../../../utils/FormBuilder/Validators";
import { studentInfoFields, addressInfoFields, permanentReminderField } from "../Fields";
import { useSetting } from "../../../contexts/SettingContext";
import { ServiceType } from "../../../interfaces/constants";

export const EditMemberForm: React.FC<{
  handleFinish: () => void;
  prevData: any;
}> = ({ handleFinish, prevData }) => {
  const [formFields, setFormFields] = React.useState<FormField[]>([]);
  const { postData, getData, typedPostData } = useFetch();
  const [editing, setEditing] = useState(false);
  // break into three sections for better readability
  const [studentFields, setStudentFields] = useState<FormField[]>(studentInfoFields());
  const [addressFields, setAddressFields] = useState<FormField[]>(addressInfoFields());
  const [reminderField, setReminderField] = useState<FormField[]>([permanentReminderField()]);
  const [patitentInfoFormContent, setPatitentInfoFormContent] = useState<React.ReactNode>(null);
  const [addressFormContent, setAddressFormContent] = useState<React.ReactNode>(null);
  const [reminderFormContent, setReminderFormContent] = useState<React.ReactNode>(null);
  const { settings } = useSetting();

  const [vipBalances, setVipBalances] = useState<Array<{ serviceId: string; serviceName: string; pricePerHour: number; hoursRemaining: number }>>([]);
  const [groupServices, setGroupServices] = useState<Array<{ serviceId: string; serviceName: string }>>([]);
  const [paymentsByService, setPaymentsByService] = useState<Record<string, any[]>>({});

  useEffect(() => {
    setFormFields([
      ...studentFields,
      ...addressFields,
      ...reminderField,
    ]);
  }, [studentFields, addressFields, reminderField]);

  const validationSchema = React.useMemo(
    () => generateValidationSchema(formFields),
    [formFields]
  );


  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange', // Validate on change
    defaultValues: {
      ...prevData,
    },
  });

  const onSubmit = async (data: any) => {
    postData('/api/member/update', { memberId: data.memberId, update: data }).then().catch().finally(() => {
      handleFinish()
    });
  };

  useEffect(() => {

    setStudentFields(studentFields.map((field) => {
      field.editable = editing;
      return field;
    }));

    setAddressFields(addressFields.map((field) => {
      field.editable = editing;
      return field;
    }));

    setReminderField(reminderField.map((field) => {
      field.editable = editing;
      return field;
    }));
    
  }, [editing]);


  useEffect(() => {
    setPatitentInfoFormContent(FormBuilder(studentFields, control, errors));
    setAddressFormContent(FormBuilder(addressFields, control, errors));
    setReminderFormContent(FormBuilder(reminderField, control, errors));
  }, [studentFields, addressFields, reminderField, control, errors]);

  useEffect(() => {
    const loadAll = async () => {
      // Fetch all payments for this member and group by serviceId
      try {
        const allPayments = await typedPostData<{ memberId: string }, any[]>('/api/payment/listByMember', { memberId: prevData.memberId });
        const grouped: Record<string, any[]> = {};
        (allPayments || []).forEach(p => {
          grouped[p.serviceId] = grouped[p.serviceId] || [];
          grouped[p.serviceId].push(p);
        });
        setPaymentsByService(grouped);
      } catch (_) {}

      const balances: Array<{ serviceId: string; serviceName: string; pricePerHour: number; hoursRemaining: number }> = [];
      const groups: Array<{ serviceId: string; serviceName: string }> = [];
      const enrolledIds: string[] = prevData?.enrolledServiceIds || [];
      for (const s of settings.services) {
        const isEnrolled = enrolledIds.includes(s.serviceId);
        if (!isEnrolled) continue;
        if (s.type === ServiceType.VIP) {
          try {
            const res = await typedPostData<{ memberId: string; serviceId: string }, { pricePerHour: number; hoursRemaining: number }>(
              '/api/payment/getVipBalance',
              { memberId: prevData.memberId, serviceId: s.serviceId }
            );
            balances.push({ serviceId: s.serviceId, serviceName: s.name, pricePerHour: res.pricePerHour, hoursRemaining: res.hoursRemaining });
          } catch (e) {
            // ignore
          }
        } else if (s.type === ServiceType.Group) {
          if ((s.studentMemberIds || []).includes(prevData.memberId)) {
            groups.push({ serviceId: s.serviceId, serviceName: s.name });
          }
        }
      }
      setVipBalances(balances);
      setGroupServices(groups);
    };
    loadAll();
  }, [settings.services, prevData]);

  const computeOriginal = (p: any, sPrice: number) => {
    if (p.pricePerHour && p.hoursPurchased) return (p.pricePerHour * p.hoursPurchased);
    return sPrice || 0;
  };

  const computeActual = (original: number, p: any) => {
    const amt = p.discountAmount || 0;
    if (p.discountType === 'percent') return original * (1 - amt / 100);
    if (p.discountType === 'fixed') return original - amt;
    return original;
  };

  const renderCombinedPaymentsTable = (serviceIds: string[]) => {
    const rows: Array<{ serviceName: string; p: any; original: number; actual: number }>[] = [] as any;
    const flat: Array<{ serviceName: string; p: any; original: number; actual: number }> = [];
    serviceIds.forEach(id => {
      const service = settings.services.find(s => s.serviceId === id);
      (paymentsByService[id] || []).forEach(p => {
        const original = computeOriginal(p, service?.price || 0);
        const actual = computeActual(original, p);
        flat.push({ serviceName: service?.name || id, p, original, actual });
      });
    });
    return (
      <TableContainer component={Paper} sx={{ mt: 1, width: '100%' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Payment Id</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Original Price</TableCell>
              <TableCell>Discount Type</TableCell>
              <TableCell align="right">Discount Value</TableCell>
              <TableCell align="right">Actual Paid</TableCell>
              <TableCell align="right">Price/Hr</TableCell>
              <TableCell align="right">Hours Purchased</TableCell>
              <TableCell align="right">Hours Remaining</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flat.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary">No payments</Typography>
                </TableCell>
              </TableRow>
            ) : flat.map(({ serviceName, p, original, actual }) => (
              <TableRow key={p.paymentId}>
                <TableCell>{serviceName}</TableCell>
                <TableCell>{p.paymentId}</TableCell>
                <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</TableCell>
                <TableCell align="right">${original.toFixed(2)}</TableCell>
                <TableCell>{p.discountType || 'none'}</TableCell>
                <TableCell align="right">{p.discountType === 'percent' ? `${(p.discountAmount || 0).toFixed(2)}%` : `$${(p.discountAmount || 0).toFixed(2)}`}</TableCell>
                <TableCell align="right">${Math.max(actual, 0).toFixed(2)}</TableCell>
                <TableCell align="right">{p.pricePerHour ?? '-'}</TableCell>
                <TableCell align="right">{p.hoursPurchased ?? '-'}</TableCell>
                <TableCell align="right">{p.hoursRemaining ?? '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="form" className="space-y-4">
      <FormSection title="Contact">
        {patitentInfoFormContent}
      </FormSection>

      <TableSection title="VIP Remaining Hours">
        <Stack direction="column" spacing={2}>
          {vipBalances.length > 0 ? (
            <>
              <Stack direction="column" spacing={0.5}>
                {vipBalances.map(item => (
                  <Typography key={item.serviceId} variant="body2">{item.serviceName} - {item.pricePerHour}/hr - Remaining: {item.hoursRemaining.toFixed(2)}h</Typography>
                ))}
              </Stack>
              {renderCombinedPaymentsTable(vipBalances.map(v => v.serviceId))}
            </>
          ) : (
            <Typography variant="body2">No VIP balances</Typography>
          )}
        </Stack>
      </TableSection>

      <TableSection title="Group Services">
        <Stack direction="column" spacing={2}>
          {groupServices.length > 0 ? (
            renderCombinedPaymentsTable(groupServices.map(g => g.serviceId))
          ) : (
            <Chip label="No group services" />
          )}
        </Stack>
      </TableSection>

      <FormSection title="Address">
        {addressFormContent}
      </FormSection>

      <SimpleFormSection title="Note">
        {reminderFormContent}
      </SimpleFormSection>

      {
        !editing &&
        <Button
          onClick={() => setEditing(true)}
          variant="contained"
          color="primary"
          className="w-full"
        >
          Edit Student Information
        </Button>
      }

      {
        editing &&
        (
          <div className="space-y-2">
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              color="primary"
              className="w-full"
            >
              Update Student Information
            </Button>

            <Button onClick={() => setEditing(false)} variant="contained" color="error" className="w-full">
              Cancel
            </Button>
          </div>
        )
      }
    </form>
  );
};

